"use client";

import clsx from "clsx";
import {useState} from "react";
import {useTranslation} from "react-i18next";
import PhoneInput from "@/components/common/PhoneInput";
import Button from "@/components/library/Button";
import Icon from "@/components/library/Icon";
import {useOrg} from "@/hooks/useOrg";

type ContactMethod = "self" | "upload" | null;

interface OutboundContactSectionProps {
   readonly onPhoneSubmit: (phoneNumber: string) => void;
   readonly onUploadClick: () => void;
   readonly isSubmitting?: boolean;
   readonly contactAdded?: boolean;
}

export function OutboundContactSection({
   onPhoneSubmit,
   onUploadClick,
   isSubmitting = false,
   contactAdded = false,
}: OutboundContactSectionProps) {
   const {t} = useTranslation("onboarding");
   const {org} = useOrg();

   const [selectedMethod, setSelectedMethod] = useState<ContactMethod>(null);
   const [phoneNumber, setPhoneNumber] = useState("");
   const [isPhoneValid, setIsPhoneValid] = useState(false);
   const [phoneError, setPhoneError] = useState(false);

   const handlePhoneSubmit = () => {
      if (!isPhoneValid) {
         setPhoneError(true);
         return;
      }
      onPhoneSubmit(phoneNumber);
   };

   // If contact is already added, show success state
   if (contactAdded) {
      return (
         <div className="space-y-4">
            {/* Section Header */}
            <div className="flex items-center gap-3">
               <div
                  className={clsx(
                     "w-10 h-10 rounded-xl flex items-center justify-center",
                     "bg-emerald-100 dark:bg-emerald-900/30"
                  )}
               >
                  <Icon icon="user-check" className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
               </div>
               <div>
                  <h3 className="font-semibold text-foreground">
                     {t("configureGail.outbound.contacts.title")}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                     {t("configureGail.outbound.contacts.subtitle")}
                  </p>
               </div>
            </div>

            {/* Success State */}
            <div
               className={clsx(
                  "rounded-2xl border border-emerald-500/50 bg-emerald-500/5 p-6",
                  "flex items-center gap-4"
               )}
            >
               <div className="w-12 h-12 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                  <Icon icon="check" className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
               </div>
               <div>
                  <p className="font-medium text-emerald-700 dark:text-emerald-300">
                     {t("configureGail.outbound.contacts.contactAdded")}
                  </p>
                  <p className="text-sm text-muted-foreground">
                     {t("configureGail.outbound.contacts.readyToLaunch")}
                  </p>
               </div>
            </div>
         </div>
      );
   }

   return (
      <div className="space-y-4">
         {/* Section Header */}
         <div className="flex items-center gap-3">
            <div
               className={clsx(
                  "w-10 h-10 rounded-xl flex items-center justify-center",
                  "bg-violet-100 dark:bg-violet-900/30"
               )}
            >
               <Icon icon="users" className="w-5 h-5 text-violet-600 dark:text-violet-400" />
            </div>
            <div>
               <h3 className="font-semibold text-foreground">
                  {t("configureGail.outbound.contacts.title")}
               </h3>
               <p className="text-sm text-muted-foreground">
                  {t("configureGail.outbound.contacts.subtitle")}
               </p>
            </div>
         </div>

         {/* Contact Options */}
         <div className="rounded-2xl border border-200 bg-panel p-6 space-y-4">
            {/* Option 1: Use your own number */}
            <button
               type="button"
               onClick={() => setSelectedMethod("self")}
               className={clsx(
                  "w-full flex items-center gap-4 p-4 rounded-xl text-left",
                  "border-2 transition-all duration-200",
                  selectedMethod === "self"
                     ? "border-primary bg-primary/5"
                     : "border-200 hover:border-primary/50 hover:bg-50 dark:hover:bg-800/50"
               )}
            >
               <div
                  className={clsx(
                     "w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0",
                     selectedMethod === "self" ? "bg-primary/20" : "bg-100 dark:bg-800"
                  )}
               >
                  <Icon
                     icon="user"
                     className={clsx(
                        "w-6 h-6",
                        selectedMethod === "self" ? "text-primary" : "text-muted-foreground"
                     )}
                  />
               </div>
               <div className="flex-1 min-w-0">
                  <p className="font-medium text-foreground">
                     {t("configureGail.outbound.contacts.selfOption.title")}
                  </p>
                  <p className="text-sm text-muted-foreground">
                     {t("configureGail.outbound.contacts.selfOption.description")}
                  </p>
               </div>
               <div
                  className={clsx(
                     "w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0",
                     selectedMethod === "self" ? "border-primary bg-primary" : "border-300"
                  )}
               >
                  {selectedMethod === "self" && <Icon icon="check" className="w-3 h-3 text-white" />}
               </div>
            </button>

            {/* Self option expanded form */}
            {selectedMethod === "self" && (
               <div className="ml-4 pl-4 border-l-2 border-primary/30 space-y-4">
                  <div className="space-y-2">
                     <p className="text-sm text-muted-foreground">
                        {t("configureGail.outbound.contacts.selfOption.instruction")}
                     </p>
                     <div className="flex gap-3 items-start">
                        <div className="flex-1">
                           <PhoneInput
                              value={phoneNumber}
                              setValue={(value) => {
                                 setPhoneNumber(value);
                                 if (phoneError) setPhoneError(false);
                              }}
                              setValid={setIsPhoneValid}
                              placeholder={t(
                                 "configureGail.outbound.contacts.selfOption.phonePlaceholder"
                              )}
                              disabled={isSubmitting}
                              error={phoneError}
                              helperText={
                                 phoneError
                                    ? t("configureGail.outbound.contacts.selfOption.phoneRequired")
                                    : undefined
                              }
                              allowAllCountries
                           />
                        </div>
                        <Button
                           type="primary"
                           onClick={handlePhoneSubmit}
                           disabled={isSubmitting || !isPhoneValid}
                           iconBefore={
                              isSubmitting ? (
                                 <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                              ) : (
                                 <Icon icon="phone" className="w-4 h-4" />
                              )
                           }
                        >
                           {isSubmitting
                              ? t("configureGail.outbound.contacts.selfOption.adding")
                              : t("configureGail.outbound.contacts.selfOption.addMe")}
                        </Button>
                     </div>
                  </div>

                  {/* Hint about using owner info */}
                  {org?.owner?.firstName && (
                     <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                        <Icon icon="circle-info" className="w-3 h-3" />
                        {t("configureGail.outbound.contacts.selfOption.hint", {
                           name: org.owner.firstName,
                        })}
                     </p>
                  )}
               </div>
            )}

            {/* Divider */}
            <div className="flex items-center gap-4">
               <div className="flex-1 h-px bg-200" />
               <span className="text-xs text-muted-foreground uppercase tracking-wide">
                  {t("configureGail.outbound.contacts.or")}
               </span>
               <div className="flex-1 h-px bg-200" />
            </div>

            {/* Option 2: Upload a list */}
            <button
               type="button"
               onClick={() => {
                  setSelectedMethod("upload");
                  onUploadClick();
               }}
               className={clsx(
                  "w-full flex items-center gap-4 p-4 rounded-xl text-left",
                  "border-2 transition-all duration-200",
                  selectedMethod === "upload"
                     ? "border-primary bg-primary/5"
                     : "border-200 hover:border-primary/50 hover:bg-50 dark:hover:bg-800/50"
               )}
            >
               <div
                  className={clsx(
                     "w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0",
                     selectedMethod === "upload" ? "bg-primary/20" : "bg-100 dark:bg-800"
                  )}
               >
                  <Icon
                     icon="upload"
                     className={clsx(
                        "w-6 h-6",
                        selectedMethod === "upload" ? "text-primary" : "text-muted-foreground"
                     )}
                  />
               </div>
               <div className="flex-1 min-w-0">
                  <p className="font-medium text-foreground">
                     {t("configureGail.outbound.contacts.uploadOption.title")}
                  </p>
                  <p className="text-sm text-muted-foreground">
                     {t("configureGail.outbound.contacts.uploadOption.description")}
                  </p>
               </div>
               <Icon icon="chevron-right" className="w-4 h-4 text-muted-foreground flex-shrink-0" />
            </button>
         </div>
      </div>
   );
}
